import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  CmpNewsletterAssetUploadMethodEnum,
  CmpNewsletterAssetUploadPurposeEnum,
  useCreateCmpNewsletterAttachmentUploadMutation,
  useGetAllCategoriesQuery,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import { ErrorBanner } from "../../common/dashboard/ErrorBanner";
import { FormPageShell } from "../../common/dashboard/FormPageShell";
import { StepProgress } from "../../common/dashboard/StepProgress";
import { usePageHeaderOverride } from "../../Layout/PageContainer";
import {
  buildSlugFromSubject,
  formatAttachmentFileSize,
  getEmailAudienceSelectionKey,
  splitDirectRecipients,
} from "./emailBackendMappers";
import {
  isEmailImageFile,
  prepareEmailAssetForUpload,
} from "./emailAssetUpload";
import {
  NewsletterTestSendPreview,
  NewsletterTestSendResult,
} from "./emailTestSend";
import { UpdateEmailBuilderState } from "./emailBuilderData";
import {
  EmailAttachment,
  EmailAudienceMemberOption,
  EmailFormState,
  RecipientGroup,
  RecipientPreviewSummary,
  RecipientSourceId,
  buildDefaultNewsletterMemberFilters,
  cloneEmailForm,
} from "./emailMockData";
import {
  AudienceFilterId,
  EmailFormMode,
  buildAudienceSummaryRows,
  getFilteredMemberIds,
  isAllowedEmailAttachment,
  maxAttachmentBytes,
  maxAttachmentCount,
  maxTotalAttachmentBytes,
  memberMatchesRecipientSource,
  pageDescriptionClassName,
  pageTitleClassName,
  primaryButtonClassName,
  recipientCountRequiresBackendPreview,
  resolveIntendedRecipientCount,
  secondaryButtonClassName,
} from "./emailWizardShared";
import StepOneAudience from "./StepOneAudience";
import StepTwoDetails from "./StepTwoDetails";
import StepThreeContent from "./StepThreeContent";
import StepFourReview from "./StepFourReview";

type EmailFormProps = {
  mode: EmailFormMode;
  initialEmail: EmailFormState;
  recipientGroups: RecipientGroup[];
  memberOptions?: EmailAudienceMemberOption[];
  isLoading?: boolean;
  isSaving?: boolean;
  onSubmit: (
    email: EmailFormState,
    options?: { stayOnCurrentPage?: boolean }
  ) => Promise<EmailFormState | void>;
  onArchiveRecipientGroup: (groupId: string) => Promise<void>;
  onPreviewRecipients: (
    email: EmailFormState
  ) => Promise<RecipientPreviewSummary | null>;
  onDuplicateAsDraft?: (email: EmailFormState) => Promise<void>;
  onPublishNewsroom?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onUnpublishNewsroom?: (
    email: EmailFormState
  ) => Promise<EmailFormState | void>;
  onPreviewTestSend?: (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string
  ) => Promise<NewsletterTestSendPreview>;
  onSendReview?: (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => Promise<
    NewsletterTestSendResult & { email?: EmailFormState | null }
  >;
  onSendNow?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onSchedule?: (email: EmailFormState) => Promise<EmailFormState | void>;
  onCancelSchedule?: (email: EmailFormState) => Promise<EmailFormState | void>;
};

type WizardStep = 1 | 2 | 3 | 4;
type NoticeState = {
  severity: "success" | "info" | "warning" | "error";
  message: string;
} | null;

type AudiencePreviewResult = {
  selectionKey: string;
  summary: RecipientPreviewSummary;
};

type AudiencePreviewError = {
  selectionKey: string;
  message: string;
};

const audiencePreviewDebounceMilliseconds = 400;

const steps: Array<{ id: WizardStep; title: string }> = [
  { id: 1, title: "Audience & Campaign Type" },
  { id: 2, title: "Campaign Details" },
  { id: 3, title: "Content Builder" },
  { id: 4, title: "Review & Send" },
];

const EmailForm = ({
  mode,
  initialEmail,
  recipientGroups,
  memberOptions = [],
  isLoading = false,
  isSaving = false,
  onSubmit,
  onArchiveRecipientGroup,
  onPreviewRecipients,
  onDuplicateAsDraft,
  onPublishNewsroom,
  onUnpublishNewsroom,
  onPreviewTestSend,
  onSendReview,
  onSendNow,
  onSchedule,
  onCancelSchedule,
}: EmailFormProps) => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [createCmpNewsletterAttachmentUpload] =
    useCreateCmpNewsletterAttachmentUploadMutation();
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const [email, setEmail] = useState<EmailFormState>(
    cloneEmailForm(initialEmail)
  );
  const [lastSavedEmail, setLastSavedEmail] = useState<EmailFormState>(
    cloneEmailForm(initialEmail)
  );
  const [step, setStep] = useState<WizardStep>(1);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [localActionLoading, setLocalActionLoading] = useState(false);
  const [activeAssetUploadCount, setActiveAssetUploadCount] = useState(0);
  const [audiencePreviewResult, setAudiencePreviewResult] =
    useState<AudiencePreviewResult | null>(null);
  const [audiencePreviewError, setAudiencePreviewError] =
    useState<AudiencePreviewError | null>(null);
  const [pendingAudiencePreviewKey, setPendingAudiencePreviewKey] =
    useState<string | null>(null);
  const latestAudiencePreviewRequestIdRef = useRef(0);
  const preparedTestSendRef = useRef<{
    clientRequestId: string;
    confirmationToken: string;
    recipientEmails: string[];
    savedEmail: EmailFormState;
  } | null>(null);
  const initialEmailResetKey = `${mode}:${initialEmail.id || "create"}`;
  const loadedEmailResetKeyRef = useRef(initialEmailResetKey);
  const latestEmailRef = useRef(email);
  const onPreviewRecipientsRef = useRef(onPreviewRecipients);
  latestEmailRef.current = email;
  onPreviewRecipientsRef.current = onPreviewRecipients;

  useEffect(() => {
    if (loadedEmailResetKeyRef.current === initialEmailResetKey) return;
    loadedEmailResetKeyRef.current = initialEmailResetKey;
    setEmail(cloneEmailForm(initialEmail));
    setLastSavedEmail(cloneEmailForm(initialEmail));
    setAudiencePreviewResult(null);
    setAudiencePreviewError(null);
    setPendingAudiencePreviewKey(null);
    setErrors({});
    setStep(1);
  }, [initialEmail, initialEmailResetKey]);

  const isBusy =
    isLoading || isSaving || localActionLoading || activeAssetUploadCount > 0;
  const isSentRecord = email.status === "sent" || email.status === "sending";
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(email) !== JSON.stringify(lastSavedEmail),
    [email, lastSavedEmail]
  );
  const directRecipientCount = splitDirectRecipients(email.directRecipients).length;
  const groupRecipientCount = recipientGroups
    .filter((group) => email.recipientGroupIds.includes(group.id))
    .reduce((total, group) => total + group.count, 0);
  const audienceSelectionKey = getEmailAudienceSelectionKey(email);
  const previewSummary =
    audiencePreviewResult?.selectionKey === audienceSelectionKey
      ? audiencePreviewResult.summary
      : null;
  const currentAudiencePreviewError =
    audiencePreviewError?.selectionKey === audienceSelectionKey
      ? audiencePreviewError.message
      : "";
  const isAudiencePreviewLoading =
    pendingAudiencePreviewKey === audienceSelectionKey;
  // Live local estimate so "Estimated Recipients" reacts to audience/source/filter
  // changes before the backend preview runs (backend preview stays the truth).
  const localEstimatedMemberCount = useMemo(() => {
    const filterSet = new Set(
      getFilteredMemberIds({
        filters: email.memberFilters,
        memberOptions,
      })
    );
    const ids = new Set<string>();

    memberOptions
      .filter(
        (member) =>
          filterSet.has(member.id) &&
          email.recipientSourceIds.some((sourceId) =>
            memberMatchesRecipientSource({ member, sourceId })
          )
      )
      .forEach((member) => ids.add(member.id));

    // Explicitly selected members remain a fixed audience and are not narrowed
    // by continuously managed member filters.
    email.selectedMemberIds.forEach((memberId) => ids.add(memberId));
    return ids.size;
  }, [
    email.memberFilters,
    email.recipientSourceIds,
    email.selectedMemberIds,
    memberOptions,
  ]);
  const hasRecipients =
    email.recipientSourceIds.filter((sourceId) => sourceId !== "selectedMembers")
      .length > 0 ||
    email.recipientGroupIds.length > 0 ||
    email.selectedMemberIds.length > 0 ||
    directRecipientCount > 0;
  const intendedRecipientCount = resolveIntendedRecipientCount({
    fallbackCount:
      localEstimatedMemberCount + groupRecipientCount + directRecipientCount,
    hasRecipients,
    isSentRecord,
    previewCount: previewSummary?.uniqueValidRecipientCount,
    requiresBackendPreview: recipientCountRequiresBackendPreview(
      email.recipientSourceIds
    ),
    storedCount: email.estimatedRecipientCount,
  });

  useEffect(() => {
    const requestId = latestAudiencePreviewRequestIdRef.current + 1;
    latestAudiencePreviewRequestIdRef.current = requestId;

    if (!hasRecipients || isLoading || isSentRecord) {
      setPendingAudiencePreviewKey(null);
      setAudiencePreviewError(null);
      if (!hasRecipients) setAudiencePreviewResult(null);
      return;
    }

    const emailSnapshot = cloneEmailForm(latestEmailRef.current);
    setPendingAudiencePreviewKey(audienceSelectionKey);
    setAudiencePreviewError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const summary = await onPreviewRecipientsRef.current(emailSnapshot);
        if (latestAudiencePreviewRequestIdRef.current !== requestId) return;
        if (!summary) {
          throw new Error("Recipient preview did not return a summary.");
        }
        setAudiencePreviewResult({
          selectionKey: audienceSelectionKey,
          summary,
        });
        setAudiencePreviewError(null);
      } catch (error) {
        if (latestAudiencePreviewRequestIdRef.current !== requestId) return;
        setAudiencePreviewError({
          selectionKey: audienceSelectionKey,
          message:
            error instanceof Error
              ? error.message
              : "Recipient estimate could not be refreshed.",
        });
      } finally {
        if (latestAudiencePreviewRequestIdRef.current === requestId) {
          setPendingAudiencePreviewKey(null);
        }
      }
    }, audiencePreviewDebounceMilliseconds);

    return () => {
      window.clearTimeout(timeoutId);
      if (latestAudiencePreviewRequestIdRef.current === requestId) {
        // Invalidate an in-flight request so a slower, older selection can
        // never overwrite the current audience preview.
        latestAudiencePreviewRequestIdRef.current += 1;
      }
    };
  }, [
    audienceSelectionKey,
    hasRecipients,
    isLoading,
    isSentRecord,
  ]);

  const hasScheduleParts = !!email.scheduledDate && !!email.scheduledTime;
  const canRunSavedBackendAction = !!email.id;
  const memberTypeOptions = useMemo(
    () => {
      const membershipTypeNamesById = new Map<string, string>();
      memberOptions.forEach((member) => {
        if (member.membershipTypeId && member.membershipTypeName) {
          membershipTypeNamesById.set(
            member.membershipTypeId,
            member.membershipTypeName
          );
        }
      });
      return Array.from(membershipTypeNamesById.entries()).map(
        ([id, label]) => ({ id, label })
      );
    },
    [memberOptions]
  );
  const industryLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    (categoriesData?.getAllCategories || []).forEach((category) => {
      labels[category.id] = category.name;
    });
    memberTypeOptions.forEach((membershipType) => {
      labels[`membershipType:${membershipType.id}`] = membershipType.label;
    });
    return labels;
  }, [categoriesData?.getAllCategories, memberTypeOptions]);
  const audienceSummaryRows = useMemo(
    () =>
      buildAudienceSummaryRows({
        email,
        recipientGroups,
        memberOptions,
        audienceFilters: email.memberFilters,
        industryLabels,
      }),
    [
      email,
      industryLabels,
      memberOptions,
      recipientGroups,
    ]
  );

  const updateEmailField = <FieldName extends keyof EmailFormState>(
    field: FieldName,
    value: EmailFormState[FieldName]
  ) => {
    setEmail((currentEmail) => ({ ...currentEmail, [field]: value }));
  };

  const updateBuilderState: UpdateEmailBuilderState = useCallback((update) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      builderState:
        typeof update === "function"
          ? update(currentEmail.builderState)
          : update,
    }));
  }, []);

  // Every email asset uses one upload boundary. Image files are rasterized,
  // resized, metadata-stripped, and converted to PNG/JPEG before the backend
  // routes them to an isolated chamber prefix in the existing media bucket.
  const uploadEmailAsset = useCallback(
    async (
      file: File,
      purpose: CmpNewsletterAssetUploadPurposeEnum,
      validatePreparedFile?: (preparedFile: File) => void
    ) => {
      if (!chamberId) {
        throw new Error("Chamber context is still loading. Try again in a moment.");
      }

      setActiveAssetUploadCount((currentCount) => currentCount + 1);
      try {
        const uploadFile = await prepareEmailAssetForUpload({ file });
        validatePreparedFile?.(uploadFile);
        const result = await createCmpNewsletterAttachmentUpload({
          variables: {
            input: {
              chamberId,
              purpose,
              fileName: uploadFile.name,
              mimeType: uploadFile.type,
              sizeBytes: uploadFile.size,
            },
          },
        });
        const upload = result.data?.createCmpNewsletterAttachmentUpload;
        if (!upload) {
          throw new Error("The email asset upload could not be prepared.");
        }
        if (upload.uploadMethod !== CmpNewsletterAssetUploadMethodEnum.Put) {
          throw new Error("The email asset upload method is not supported.");
        }
        const uploadResponse = await fetch(upload.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": uploadFile.type },
          body: uploadFile,
        });
        if (!uploadResponse.ok) {
          throw new Error("The email asset could not be uploaded.");
        }
        return upload;
      } finally {
        setActiveAssetUploadCount((currentCount) =>
          Math.max(currentCount - 1, 0)
        );
      }
    },
    [chamberId, createCmpNewsletterAttachmentUpload]
  );

  const uploadBuilderAsset = useCallback(
    async (file: File): Promise<string> => {
      const upload = await uploadEmailAsset(
        file,
        CmpNewsletterAssetUploadPurposeEnum.BuilderImage
      );
      if (!upload.publicUrl) {
        throw new Error("The content image URL could not be prepared.");
      }
      return upload.publicUrl;
    },
    [uploadEmailAsset]
  );

  const updateAudienceFilter = (filterId: AudienceFilterId, values: string[]) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      memberFilters: {
        ...currentEmail.memberFilters,
        [filterId]: values,
      },
    }));
  };

  const clearAllFilters = () => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      memberFilters: buildDefaultNewsletterMemberFilters(),
    }));
  };

  const toggleRecipientSource = (sourceId: RecipientSourceId) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      recipientSourceIds: currentEmail.recipientSourceIds.includes(sourceId)
        ? currentEmail.recipientSourceIds.filter((id) => id !== sourceId)
        : [...currentEmail.recipientSourceIds, sourceId],
    }));
  };

  const toggleRecipientGroup = (groupId: string) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      recipientGroupIds: currentEmail.recipientGroupIds.includes(groupId)
        ? currentEmail.recipientGroupIds.filter((id) => id !== groupId)
        : [...currentEmail.recipientGroupIds, groupId],
    }));
  };

  const runAction = useCallback(async (
    action: () => Promise<EmailFormState | void>,
    successMessage: string
  ) => {
    setLocalActionLoading(true);
    try {
      const savedEmail = await action();
      const nextEmail = savedEmail ? cloneEmailForm(savedEmail) : cloneEmailForm(email);
      setEmail(nextEmail);
      setLastSavedEmail(nextEmail);
      setNotice({ severity: "success", message: successMessage });
      return nextEmail;
    } catch (error) {
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The email workflow could not be saved.",
      });
      return null;
    } finally {
      setLocalActionLoading(false);
    }
  }, [email]);

  const saveDraft = useCallback(async () => {
    await runAction(() => onSubmit(email), "Draft saved.");
  }, [email, onSubmit, runAction]);

  // The mocks use a different page subtitle once Step 3 is in the BUILDER
  // phase (template picked / canvas has blocks) vs everywhere else
  // (EMAIL-CREATE-016: replicate the per-phase subtitle as shown).
  const isBuilderPhase =
    step === 3 &&
    (!!email.templateId || email.builderState.blocks.length > 0);
  const pageDescription = isBuilderPhase
    ? "Create and send professional chamber communications, newsletters, advocacy updates, media releases, and member announcements."
    : "Create and send targeted chamber communications to the right audience, increasing email open and click-through rates with professional email templates and advanced member filtering.";

  useEffect(() => {
    setPageHeaderOverride({
      title: mode === "create" ? "Create Email" : "Update Email",
      description: pageDescription,
      titleClassName: pageTitleClassName,
      descriptionClassName: pageDescriptionClassName,
      actions: (
        <button
          type="button"
          className={primaryButtonClassName}
          onClick={saveDraft}
          disabled={isBusy || isSentRecord}
        >
          {isBusy ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            <SaveOutlinedIcon />
          )}
          Save Draft
        </button>
      ),
    });

    return () => setPageHeaderOverride(null);
  }, [isBusy, isSentRecord, mode, pageDescription, saveDraft, setPageHeaderOverride]);

  const validateStep = (stepToValidate: WizardStep) => {
    const nextErrors: Record<string, string> = {};

    if (stepToValidate === 1 && !hasRecipients) {
      nextErrors.recipients = "Select at least one audience before continuing.";
    }
    if (stepToValidate === 2) {
      if (!email.category.trim()) nextErrors.category = "Campaign name is required.";
      if (!email.subject.trim()) nextErrors.subject = "Email subject is required.";
      if (!email.fromName.trim()) nextErrors.fromName = "From name is required.";
      if (!email.fromEmail.trim()) nextErrors.fromEmail = "From email is required.";
    }
    // Step 3 is complete once a template's blocks were loaded onto the canvas
    // OR the custom email canvas has at least one block (builder-shell spec).
    if (stepToValidate === 3 && email.builderState.blocks.length === 0) {
      nextErrors.content =
        "Select an email template or add at least one content block before continuing.";
    }

    return nextErrors;
  };

  const goToStep = (nextStep: WizardStep) => {
    if (nextStep > step) {
      // Validate every step between the current one and the target so a
      // stepper jump cannot skip a required step (for example landing on
      // Review & Send while the Content Builder canvas is still empty).
      for (let stepId = step; stepId < nextStep; stepId += 1) {
        const stepErrors = validateStep(stepId as WizardStep);
        if (Object.keys(stepErrors).length > 0) {
          setErrors(stepErrors);
          setStep(stepId as WizardStep);
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }
    }

    setErrors({});
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const archiveRecipientGroup = async (group: RecipientGroup) => {
    setLocalActionLoading(true);
    try {
      await onArchiveRecipientGroup(group.id);
      setEmail((currentEmail) => ({
        ...currentEmail,
        recipientGroupIds: currentEmail.recipientGroupIds.filter(
          (groupId) => groupId !== group.id
        ),
      }));
      setNotice({ severity: "success", message: `"${group.name}" was archived.` });
    } catch (error) {
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The custom audience list could not be archived.",
      });
    } finally {
      setLocalActionLoading(false);
    }
  };

  const addAttachment = async (file?: File) => {
    if (!file) return;
    if (email.attachments.length >= maxAttachmentCount) {
      setNotice({
        severity: "error",
        message: `A newsletter can have at most ${maxAttachmentCount} attachments.`,
      });
      return;
    }
    if (!isAllowedEmailAttachment({ file })) {
      setNotice({
        severity: "error",
        message: "Attachments must be a PDF, DOC, DOCX, or image file.",
      });
      return;
    }
    if (!isEmailImageFile({ file }) && file.size > maxAttachmentBytes) {
      setNotice({ severity: "error", message: "Attachments must be 6MB or smaller." });
      return;
    }
    setLocalActionLoading(true);
    try {
      const currentAttachmentBytes = email.attachments.reduce(
        (totalBytes, attachment) => totalBytes + attachment.sizeBytes,
        0
      );
      const upload = await uploadEmailAsset(
        file,
        CmpNewsletterAssetUploadPurposeEnum.Attachment,
        (preparedFile) => {
          if (
            currentAttachmentBytes + preparedFile.size >
            maxTotalAttachmentBytes
          ) {
            throw new Error(
              "Combined attachments must be 6MB or smaller so the email can be sent."
            );
          }
        }
      );
      const attachment: EmailAttachment = {
        id: upload.storageKey,
        name: upload.fileName,
        fileType: upload.mimeType,
        sizeBytes: upload.sizeBytes,
        sizeLabel: formatAttachmentFileSize(upload.sizeBytes),
        storageKey: upload.storageKey,
      };
      setEmail((currentEmail) => ({
        ...currentEmail,
        attachments: [...currentEmail.attachments, attachment],
      }));
      setNotice({ severity: "success", message: `${attachment.name} added.` });
    } catch (error) {
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The attachment could not be uploaded.",
      });
    } finally {
      setLocalActionLoading(false);
    }
  };

  const runSavedEmailAction = async (
    action: (savedEmail: EmailFormState) => Promise<EmailFormState | void>,
    successMessage: string
  ) => {
    setLocalActionLoading(true);
    let savedEmailForState: EmailFormState | null = null;

    try {
      const savedEmail = await onSubmit(email, { stayOnCurrentPage: true });
      savedEmailForState = savedEmail
        ? cloneEmailForm(savedEmail)
        : cloneEmailForm(email);
      const actionEmail = await action(savedEmailForState);
      const nextEmail = actionEmail
        ? cloneEmailForm(actionEmail)
        : cloneEmailForm(savedEmailForState);
      setEmail(nextEmail);
      setLastSavedEmail(nextEmail);
      setNotice({ severity: "success", message: successMessage });
    } catch (error) {
      if (savedEmailForState) {
        setEmail(savedEmailForState);
        setLastSavedEmail(savedEmailForState);
      }
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The email workflow could not be saved.",
      });
    } finally {
      setLocalActionLoading(false);
    }
  };

  const previewTestSend = async (
    recipientEmails: string[],
    clientRequestId: string
  ): Promise<NewsletterTestSendPreview> => {
    if (!onPreviewTestSend) {
      throw new Error("Test email preview is not available.");
    }

    setLocalActionLoading(true);
    let savedEmailForState: EmailFormState | null = null;

    try {
      const savedEmail = await onSubmit(email, { stayOnCurrentPage: true });
      savedEmailForState = savedEmail
        ? cloneEmailForm(savedEmail)
        : cloneEmailForm(email);
      setEmail(savedEmailForState);
      setLastSavedEmail(savedEmailForState);

      const preview = await onPreviewTestSend(
        savedEmailForState,
        recipientEmails,
        clientRequestId
      );
      if (preview.explicitRecipientsAllowed && !preview.confirmationToken) {
        throw new Error(
          "The server did not return a usable test-email confirmation."
        );
      }
      preparedTestSendRef.current = {
        clientRequestId,
        confirmationToken: preview.confirmationToken || "",
        recipientEmails: [...preview.normalizedRecipientEmails],
        savedEmail: cloneEmailForm(savedEmailForState),
      };
      return preview;
    } catch (error) {
      if (savedEmailForState) {
        setEmail(savedEmailForState);
        setLastSavedEmail(savedEmailForState);
      }
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The test email could not be prepared.",
      });
      throw error;
    } finally {
      setLocalActionLoading(false);
    }
  };

  const sendReview = async (
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => {
    if (!onSendReview) {
      throw new Error("Test email sending is not available.");
    }

    const preparedTestSend = preparedTestSendRef.current;
    if (
      !preparedTestSend ||
      preparedTestSend.clientRequestId !== clientRequestId ||
      preparedTestSend.confirmationToken !== confirmationToken ||
      JSON.stringify(preparedTestSend.recipientEmails) !==
        JSON.stringify(recipientEmails)
    ) {
      throw new Error(
        "The test email confirmation is no longer current. Preview it again."
      );
    }

    setLocalActionLoading(true);
    try {
      const result = await onSendReview(
        preparedTestSend.savedEmail,
        recipientEmails,
        clientRequestId,
        confirmationToken
      );
      const nextEmail = result.email
        ? cloneEmailForm(result.email)
        : cloneEmailForm(preparedTestSend.savedEmail);
      setEmail(nextEmail);
      setLastSavedEmail(nextEmail);
      preparedTestSendRef.current = null;
      setNotice({
        severity: "success",
        message: `Test email sent to ${result.actualRecipientEmails.join(", ")}.`,
      });
    } catch (error) {
      setNotice({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The test email could not be sent.",
      });
      throw error;
    } finally {
      setLocalActionLoading(false);
    }
  };

  const sendNow = async () => {
    if (!onSendNow) return;
    await runSavedEmailAction(
      (savedEmail) => onSendNow(savedEmail),
      "Email send was queued."
    );
  };

  const scheduleEmail = async () => {
    if (!onSchedule) return;
    if (!hasScheduleParts) {
      setNotice({
        severity: "warning",
        message: "Choose a schedule date and time before scheduling.",
      });
      return;
    }
    await runSavedEmailAction(
      (savedEmail) => onSchedule(savedEmail),
      "Email scheduled."
    );
  };

  const cancelSchedule = async () => {
    if (!onCancelSchedule) return;
    await runAction(() => onCancelSchedule(email), "Scheduled email canceled.");
  };

  const publishNewsroom = async () => {
    if (!onPublishNewsroom) return;
    await runAction(() => onPublishNewsroom(email), "Newsroom entry published.");
  };

  const unpublishNewsroom = async () => {
    if (!onUnpublishNewsroom) return;
    await runAction(() => onUnpublishNewsroom(email), "Newsroom entry unpublished.");
  };

  const setNewsroomToggle = (checked: boolean) => {
    setEmail((currentEmail) => ({
      ...currentEmail,
      publishToNewsroom: checked,
      newsroomSlug:
        checked && !currentEmail.newsroomSlug
          ? buildSlugFromSubject(currentEmail.subject)
          : currentEmail.newsroomSlug,
      seoTitle:
        checked && !currentEmail.seoTitle
          ? currentEmail.subject
          : currentEmail.seoTitle,
    }));
  };

  const renderActiveStep = () => {
    if (step === 1)
      return (
        <StepOneAudience
          email={email}
          recipientGroups={recipientGroups}
          memberTypeOptions={memberTypeOptions}
          audienceFilters={email.memberFilters}
          intendedRecipientCount={intendedRecipientCount}
          isAudiencePreviewLoading={isAudiencePreviewLoading}
          audiencePreviewError={currentAudiencePreviewError}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          toggleRecipientSource={toggleRecipientSource}
          toggleRecipientGroup={toggleRecipientGroup}
          archiveRecipientGroup={archiveRecipientGroup}
          updateAudienceFilter={updateAudienceFilter}
          clearAllFilters={clearAllFilters}
          updateEmailField={updateEmailField}
        />
      );
    if (step === 2)
      return (
        <StepTwoDetails
          email={email}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          updateEmailField={updateEmailField}
          updateBuilderState={updateBuilderState}
          uploadImage={uploadBuilderAsset}
        />
      );
    if (step === 3)
      return (
        <StepThreeContent
          email={email}
          isBusy={isBusy}
          isSentRecord={isSentRecord}
          updateBuilderState={updateBuilderState}
          uploadImage={uploadBuilderAsset}
          updateEmailField={updateEmailField}
        />
      );
    return (
      <StepFourReview
        mode={mode}
        email={email}
        isBusy={isBusy}
        isSentRecord={isSentRecord}
        canRunSavedBackendAction={canRunSavedBackendAction}
        intendedRecipientCount={intendedRecipientCount}
        directRecipientCount={directRecipientCount}
        audienceRows={audienceSummaryRows}
        goToCampaignDetails={() => goToStep(2)}
        updateEmailField={updateEmailField}
        setNewsroomToggle={setNewsroomToggle}
        addAttachment={addAttachment}
        onDuplicateAsDraft={onDuplicateAsDraft}
        previewTestSend={previewTestSend}
        sendReview={sendReview}
        scheduleEmail={scheduleEmail}
        cancelSchedule={cancelSchedule}
        publishNewsroom={publishNewsroom}
        unpublishNewsroom={unpublishNewsroom}
        sendNow={sendNow}
        hasSendReviewAction={!!onPreviewTestSend && !!onSendReview}
        hasScheduleAction={!!onSchedule}
        hasCancelScheduleAction={!!onCancelSchedule}
        hasPublishNewsroomAction={!!onPublishNewsroom}
        hasUnpublishNewsroomAction={!!onUnpublishNewsroom}
        hasSendNowAction={!!onSendNow}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600">
        Loading email workflow...
      </div>
    );
  }

  return (
    // B10 shell rewire (POL-001/POL-002 Emails leg): FormPageShell owns the
    // page chrome; the hand-rolled WizardStepper was replaced by the shared
    // StepProgress (all mock-bound behaviors preserved — gold circle, gold
    // chevron only after the active step, gold underline; sizing follows the
    // Members system per the Q-037a precedent). Steps 1/2/4 use the shared
    // 1180px clamp; step 3's builder grid keeps w-full (its fixed/min tracks
    // barely fit the clamp — UpdateMember w-full precedent).
    <FormPageShell
      headingId={`email-form-step-${step}`}
      heading={steps[step - 1].title}
      beforeMain={
        <UnsavedChangesPrompt
          when={hasUnsavedChanges || activeAssetUploadCount > 0}
        />
      }
      stepper={
        <StepProgress
          activeStep={step}
          steps={steps}
          ariaLabel={
            mode === "create" ? "Create email steps" : "Update email steps"
          }
          desktopGridColsClassName="lg:grid-cols-4"
          stepTestIdPrefix="email-step"
          // Shipped Emails behavior kept: the stepper is clickable in BOTH
          // create and update modes (variance vs Members' create-mode static
          // stepper, logged in the B10 audit); goToStep validates every
          // intermediate step so forward jumps cannot skip a required step.
          onStepClick={(stepNumber) => goToStep(stepNumber as WizardStep)}
        />
      }
      alerts={
        <>
          {notice ? (
            <Alert
              severity={notice.severity}
              className="mb-6"
              onClose={() => setNotice(null)}
            >
              {notice.message}
            </Alert>
          ) : null}
          <ErrorBanner messages={Object.values(errors)} className="mb-6" />
        </>
      }
      actionBarAlert={
        // SH-006 dual-position pattern: the validation banner renders above
        // the step content AND above the footer button row.
        <ErrorBanner messages={Object.values(errors)} className="mb-4" />
      }
      actionBar={
        <>
          {step === 1 ? (
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => goToStep((step - 1) as WizardStep)}
              disabled={isBusy}
            >
              <ChevronLeftIcon />
              Previous Step
            </button>
          )}
          {step < 4 ? (
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={() => goToStep((step + 1) as WizardStep)}
              disabled={isBusy}
            >
              Next Step: {steps[step].title}
              <ArrowForwardIcon />
            </button>
          ) : null}
        </>
      }
      mainClassName={step === 3 ? "w-full" : undefined}
    >
      {renderActiveStep()}
    </FormPageShell>
  );
};

export default EmailForm;
